<script setup lang="ts" generic="TId extends string">
import type { TabElement } from "@/types/Misc";
import BaseButton from "~/components/base/interaction/BaseButton/BaseButton.vue";

const {
  modelValue = undefined,
  navigate = true,
  tabs,
  variant = "fullWidthPage",
} = defineProps<{
  tabs: TabElement<TId>[];
  modelValue?: TId;
  navigate?: boolean;
  variant?: "fullWidthPage" | "contained";
  isStickyDisabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", tabId: TId): void;
}>();

type Slots = {
  default(): HTMLElement;
  actions?(props: { active: TId }): HTMLElement;
};

defineSlots<Slots>();

const router = useRouter();
const route = useRoute();
const localePath = useLocalePath();

type ButtonRef = {
  element: InstanceType<typeof BaseButton>;
  tabId: TId;
};

// Typing as a generic causes typign issues https://github.com/vuejs/core/issues/2136
const tabRefs: Ref<ButtonRef[]> = ref([]);
const tabsScrollContainer = useTemplateRef("tabsScrollContainer");
onMounted(() => {
  // Don't scroll into view if the first tab is selected
  if (tabs?.[0]?.id === modelValue) return;

  const activeTab = tabRefs.value.find((tabRef) => tabRef.tabId === modelValue);

  // Scroll the active tab into the view
  const activeTabLeft = activeTab?.element.$el.offsetLeft;
  tabsScrollContainer.value?.scrollTo({
    left: activeTabLeft - 20,
  });
});

const onTabClick = (tabId: TId): void => {
  emit("update:modelValue", tabId);

  if (navigate)
    router.replace(
      localePath({
        path: route.path,
        query: {
          ...route.query,
          tab: tabId,
          sort: undefined,
        },
      }),
    );
};

const isLastTabSelected = computed(() => {
  const lastTab = tabs.at(-1);

  return lastTab?.id === modelValue;
});
</script>

<template>
  <BaseStickyHeader
    :sticky-disabled="isStickyDisabled"
    :static-header-class="
      variant === 'contained' ? 'w-full' : 'block-container'
    "
  >
    <div
      v-bind="$attrs"
      class="border-b w-full bg-platform-bg-1 z-10"
      :class="[
        variant === 'contained'
          ? 'border-none md:pb-4'
          : 'block-container py-2',
      ]"
    >
      <div class="w-full px-0 site-viewport-container relative">
        <div
          ref="tabsScrollContainer"
          data-test="tabs-scroll-container"
          class="overflow-x-auto flex scroll-pl-4 md:scroll-pl-0"
          :class="{ 'w-[calc(100%-50px)] md:w-full': $slots.actions }"
        >
          <div
            class="flex justify-between horizontal-tabs__nav-container"
            :class="[
              {
                'horizontal-tabs__nav-container--no-actions': !$slots.actions,
                'horizontal-tabs__nav-container--no-shadow':
                  !$slots.actions && isLastTabSelected,
              },
              variant === 'contained' ? 'py-0 md:py-2' : 'py-2',
            ]"
          >
            <nav
              class="mb-px flex space-x-3 min-w-fit font-bold border-solid items-center md:mr-4"
              :class="[variant === 'contained' ? 'px-0' : 'px-4 md:px-0']"
              aria-label="Tabs"
            >
              <BaseButton
                v-for="tab in tabs"
                :key="tab.title"
                :ref="
                  (el) =>
                    tabRefs.push({
                      element: el as InstanceType<typeof BaseButton>,
                      tabId: tab.id,
                    })
                "
                size="responsive"
                :disabled="tab.isDisabled"
                class="whitespace-nowrap"
                :color-scheme="
                  tab.id === modelValue ? 'primary' : 'transparent'
                "
                :class="[
                  tab.id === modelValue ? 'cursor-default' : 'cursor-pointer',
                  tab.isDisabled ? 'opacity-50' : '',
                ]"
                :aria-current="tab.id === modelValue ? 'page' : undefined"
                :data-cy="`tab-${tab.id}`"
                :data-test="`tab-${tab.id}`"
                @click="onTabClick(tab.id)"
              >
                {{ tab.title }}
              </BaseButton>
            </nav>
          </div>
          <div
            v-if="$slots.actions"
            class="flex items-center absolute min-h-full right-4 md:static md:transform-none md:py-2 md:ml-auto"
          >
            <slot
              :active="modelValue as TId"
              name="actions"
            />
          </div>
        </div>
      </div>
    </div>
  </BaseStickyHeader>
  <div class="block-container">
    <slot />
  </div>
</template>

<style lang="postcss" scoped>
.horizontal-tabs {
  &__nav-container {
    &::after {
      content: "";
      @apply block md:hidden;
      @apply absolute right-[50px] top-0 md:right-0;
      @apply pointer-events-none h-full w-8;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(var(--background-1-rgb), 1) 100%
      );
    }

    &--no-actions {
      &::after {
        @apply right-0;
      }
    }

    &--no-shadow {
      &::after {
        @apply hidden;
      }
    }
  }
}
</style>
